/**
 * FollowSuggestions — Horizontal scroll of suggested lifters to follow.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { getFollowSuggestions, type SuggestedProfile } from '@/lib/followSuggestions';
import FollowButton from './FollowButton';

export default function FollowSuggestions() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const router = useRouter();
  const [suggestions, setSuggestions] = useState<SuggestedProfile[]>([]);

  useEffect(() => {
    if (!profile?.id) return;
    getFollowSuggestions(
      profile.id,
      profile.home_gym_id || profile.gym_id
    ).then(setSuggestions);
  }, [profile?.id, profile?.home_gym_id, profile?.gym_id]);

  if (suggestions.length === 0) return null;

  return (
    <View style={styles.container}>
      <Text style={[styles.heading, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
        SUGGESTED LIFTERS
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.scroll}>
        {suggestions.map((user) => (
          <TouchableOpacity
            key={user.id}
            style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}
            onPress={() => router.push(`/u/${user.handle}` as any)}
            activeOpacity={0.7}
          >
            {user.avatarUrl ? (
              <Image source={{ uri: user.avatarUrl }} style={[styles.avatar, { borderColor: c.border }]} />
            ) : (
              <View style={[styles.avatar, styles.avatarPlaceholder, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}>
                <Text style={[styles.avatarInitial, { color: c.textSecondary }]}>
                  {(user.displayName || '?')[0].toUpperCase()}
                </Text>
              </View>
            )}
            <Text style={[styles.name, { color: c.text, fontFamily: TYPO.body.semibold }]} numberOfLines={1}>
              {user.displayName}
            </Text>
            <Text style={[styles.handle, { color: c.textTertiary }]} numberOfLines={1}>
              @{user.handle}
            </Text>
            <FollowButton targetUserId={user.id} compact />
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: SPACE.sm },
  heading: { fontSize: 11, letterSpacing: 1.2 },
  scroll: { gap: SPACE.sm, paddingRight: SPACE.sm },
  card: {
    width: 120,
    padding: 12,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
    alignItems: 'center',
    gap: 6,
  },
  avatar: { width: 44, height: 44, borderRadius: 22, borderWidth: 1 },
  avatarPlaceholder: { alignItems: 'center', justifyContent: 'center' },
  avatarInitial: { fontSize: 16, fontWeight: '700' },
  name: { fontSize: FONT.sm, textAlign: 'center' },
  handle: { fontSize: 11, textAlign: 'center' },
});
